/* A city name is better evidence of a state than the state's own abbreviation.
 *
 * matrixheadstart.org's About page says "Detroit" five times and never says
 * "MI" or "Michigan" once. Every operating-state signal the reader had was a
 * two-letter token — the weakest evidence in any document, and the one that
 * made "CLAIMS FIRST MADE DURING THE POLICY PERIOD, OR ANY" read as Oregon.
 * "Detroit" has none of that ambiguity: it is not an English word, not a form
 * prefix, not a conjunction, and it belongs to exactly one state.
 *
 * WHAT IS IN THE LIST, AND WHAT IS DELIBERATELY NOT
 *
 * Only cities whose name is dominated by ONE state. Every name that a second
 * state has a real claim on is omitted, however large the city: Portland
 * (OR/ME), Springfield (IL/MA/MO/OH), Columbus (OH/GA), Kansas City (MO/KS),
 * Charleston (SC/WV), Arlington (TX/VA), Aurora (CO/IL), Glendale (AZ/CA),
 * Pasadena (CA/TX), Richmond (VA/CA), Rochester (NY/MN), Columbia (SC/MO),
 * Jackson (MS/TN), Lafayette (LA/IN), Bloomington (IN/MN/IL), Fayetteville
 * (NC/AR), Wilmington (DE/NC), Newark (NJ/OH/DE), Cleveland (OH/TN).
 *
 * Also omitted: every city whose name is an ordinary English word or a common
 * proper noun that appears in insurance prose — Mobile, Surprise, Sparks,
 * Spring, Reading, Independence, Orange, Corona, Union, Madison, Franklin,
 * Salem, Concord, Dover, Auburn, Athens, Cambridge, Manhattan, Hollywood.
 * A gazetteer that fires on "reading the policy" is the abbreviation problem
 * again with more syllables.
 *
 * The bar for inclusion is: would a broker reading this line in a report agree,
 * without checking, that this city means this state? If not, it is not here.
 * The list is therefore deliberately incomplete. A city it misses costs a
 * signal we never had; a city it gets wrong costs a wrong state on a report.
 */
(function (root) {
  const CITIES = {
    AL: ['Birmingham', 'Montgomery', 'Tuscaloosa', 'Auburn University', 'Hoover', 'Dothan', 'Gadsden'],
    AK: ['Anchorage', 'Fairbanks', 'Juneau', 'Ketchikan', 'Sitka'],
    AZ: ['Phoenix', 'Tucson', 'Scottsdale', 'Tempe', 'Mesa', 'Chandler', 'Yuma', 'Flagstaff', 'Sedona', 'Sierra Vista'],
    AR: ['Little Rock', 'Fort Smith', 'Jonesboro', 'Bentonville', 'Conway', 'Texarkana'],
    CA: ['Los Angeles', 'San Francisco', 'San Diego', 'San Jose', 'Sacramento', 'Fresno', 'Oakland',
         'Long Beach', 'Bakersfield', 'Anaheim', 'Santa Ana', 'Stockton', 'Irvine', 'Chula Vista',
         'San Bernardino', 'Modesto', 'Oxnard', 'Fontana', 'Moreno Valley', 'Huntington Beach',
         'Santa Clarita', 'Garden Grove', 'Oceanside', 'Rancho Cucamonga', 'Elk Grove', 'Palmdale',
         'Salinas', 'Hayward', 'Pomona', 'Escondido', 'Sunnyvale', 'Torrance', 'Fullerton',
         'Thousand Oaks', 'Visalia', 'Simi Valley', 'Roseville', 'Santa Clara', 'Vallejo',
         'Victorville', 'Berkeley', 'Murrieta', 'Burbank', 'Temecula', 'Daly City', 'Santa Maria',
         'El Monte', 'Downey', 'Costa Mesa', 'Inglewood', 'Rialto', 'San Mateo', 'Compton',
         'Mission Viejo', 'Cupertino', 'Palo Alto', 'Malibu', 'Beverly Hills', 'Santa Monica',
         'Redondo Beach', 'Menlo Park', 'Mountain View', 'Walnut Creek', 'San Luis Obispo'],
    CO: ['Denver', 'Colorado Springs', 'Fort Collins', 'Thornton', 'Arvada', 'Pueblo', 'Boulder',
         'Greeley', 'Longmont', 'Broomfield', 'Castle Rock', 'Littleton', 'Aspen', 'Vail',
         'Durango', 'Grand Junction', 'Steamboat Springs', 'Centennial', 'Englewood'],
    CT: ['Hartford', 'New Haven', 'Bridgeport', 'Stamford', 'Waterbury', 'Norwalk', 'Danbury',
         'New Britain', 'West Hartford', 'Greenwich'],
    DE: ['Wilmington, Delaware', 'Rehoboth Beach'],
    FL: ['Jacksonville', 'Miami', 'Tampa', 'Orlando', 'St. Petersburg', 'Hialeah', 'Tallahassee',
         'Fort Lauderdale', 'Cape Coral', 'Pembroke Pines', 'Miramar', 'Coral Springs', 'Palm Bay',
         'West Palm Beach', 'Clearwater', 'Lakeland', 'Pompano Beach', 'Boca Raton', 'Sarasota',
         'Kissimmee', 'Daytona Beach', 'Boynton Beach', 'Delray Beach', 'Doral', 'Tamarac',
         'Port St. Lucie', 'Coral Gables', 'Key West', 'Wellington', 'Deltona', 'Deerfield Beach',
         'Pensacola', 'Panama City', 'Ocala', 'Bradenton', 'Altamonte Springs', 'Winter Park'],
    GA: ['Atlanta', 'Savannah', 'Macon', 'Alpharetta', 'Marietta', 'Sandy Springs', 'Valdosta',
         'Warner Robins', 'Kennesaw', 'Dunwoody', 'Peachtree City', 'Milledgeville'],
    HI: ['Honolulu', 'Hilo', 'Kailua', 'Waikiki', 'Pearl City', 'Kaneohe', 'Lahaina'],
    ID: ['Boise', 'Nampa', 'Idaho Falls', "Coeur d'Alene", 'Pocatello', 'Twin Falls', 'Caldwell'],
    IL: ['Chicago', 'Rockford', 'Joliet', 'Naperville', 'Elgin', 'Waukegan', 'Cicero', 'Champaign',
         'Evanston', 'Schaumburg', 'Arlington Heights', 'Skokie', 'Des Plaines', 'Orland Park',
         'Berwyn', 'Oak Lawn', 'Palatine', 'Tinley Park', 'Oak Brook', 'Naperville'],
    IN: ['Indianapolis', 'Fort Wayne', 'Evansville', 'South Bend', 'Fishers', 'Muncie',
         'Terre Haute', 'Kokomo', 'Elkhart', 'Noblesville', 'Mishawaka', 'West Lafayette'],
    IA: ['Des Moines', 'Cedar Rapids', 'Davenport', 'Sioux City', 'Iowa City', 'Ames',
         'Council Bluffs', 'Dubuque', 'Cedar Falls', 'Ankeny'],
    KS: ['Wichita', 'Overland Park', 'Olathe', 'Topeka', 'Salina', 'Hutchinson', 'Leawood'],
    KY: ['Louisville', 'Owensboro', 'Paducah', 'Elizabethtown', 'Hopkinsville'],
    LA: ['New Orleans', 'Baton Rouge', 'Shreveport', 'Lake Charles', 'Kenner', 'Bossier City',
         'Metairie', 'Slidell', 'Houma', 'Thibodaux', 'Ruston'],
    ME: ['Bangor', 'Biddeford', 'Kennebunkport', 'Bar Harbor', 'Presque Isle', 'Old Orchard Beach'],
    MD: ['Baltimore', 'Silver Spring', 'Waldorf', 'Rockville', 'Gaithersburg', 'Bowie', 'Annapolis',
         'Bethesda', 'Towson', 'Hagerstown', 'Catonsville', 'Glen Burnie'],
    MA: ['Boston', 'Worcester', 'Lowell', 'Brockton', 'New Bedford', 'Fall River', 'Framingham',
         'Haverhill', 'Waltham', 'Malden', 'Brookline', 'Chicopee', 'Pittsfield', 'Somerville',
         'Revere', 'Medford, MA', 'Woburn', 'Lexington, MA'],
    MI: ['Detroit', 'Grand Rapids', 'Sterling Heights', 'Ann Arbor', 'Lansing', 'Flint', 'Dearborn',
         'Livonia', 'Westland', 'Farmington Hills', 'Kalamazoo', 'Southfield', 'Rochester Hills',
         'Saginaw', 'Pontiac', 'Novi', 'Royal Oak', 'Battle Creek', 'Muskegon', 'Traverse City',
         'Bay City', 'Grosse Pointe', 'Bloomfield Hills', 'East Lansing', 'Ypsilanti',
         'Port Huron', 'Kentwood', 'Wyandotte', 'Hamtramck', 'Dearborn Heights'],
    MN: ['Minneapolis', 'St. Paul', 'Saint Paul', 'Brooklyn Park', 'Maple Grove', 'Eagan',
         'Eden Prairie', 'Edina', 'Minnetonka', 'Burnsville', 'Mankato', 'Moorhead', 'Coon Rapids'],
    MS: ['Gulfport', 'Southaven', 'Hattiesburg', 'Biloxi', 'Tupelo', 'Starkville', 'Vicksburg',
         'Natchez', 'Olive Branch'],
    MO: ['St. Louis', 'Saint Louis', "Lee's Summit", 'Joplin', 'Jefferson City', 'Branson',
         'Cape Girardeau', 'Ballwin', 'Kirkwood'],
    MT: ['Billings', 'Missoula', 'Great Falls', 'Bozeman', 'Butte', 'Kalispell', 'Whitefish'],
    NE: ['Omaha', 'Grand Island', 'Kearney', 'Papillion', 'Scottsbluff', 'La Vista'],
    NV: ['Las Vegas', 'Reno', 'North Las Vegas', 'Carson City', 'Elko', 'Summerlin'],
    NH: ['Nashua', 'Keene', 'Merrimack', 'Londonderry'],
    NJ: ['Jersey City', 'Paterson', 'Edison', 'Toms River', 'Trenton', 'Clifton', 'Cherry Hill',
         'Passaic', 'Bayonne', 'East Orange', 'Vineland', 'Hoboken', 'Atlantic City',
         'Parsippany', 'Fort Lee', 'Montclair', 'New Brunswick', 'Perth Amboy', 'Sayreville'],
    NM: ['Albuquerque', 'Las Cruces', 'Rio Rancho', 'Santa Fe', 'Taos', 'Los Alamos',
         'Alamogordo', 'Clovis', 'Hobbs'],
    NY: ['New York', 'Buffalo', 'Yonkers', 'Syracuse', 'New Rochelle', 'Schenectady', 'Utica',
         'White Plains', 'Hempstead', 'Niagara Falls', 'Binghamton', 'Poughkeepsie', 'Brooklyn',
         'the Bronx', 'Staten Island', 'Saratoga Springs', 'Long Island City', 'Elmira',
         'Yonkers', 'Levittown, NY', 'Scarsdale', 'Rye Brook'],
    NC: ['Charlotte', 'Raleigh', 'Greensboro', 'Winston-Salem', 'Cary', 'High Point', 'Asheville',
         'Chapel Hill', 'Gastonia', 'Rocky Mount', 'Huntersville', 'Kannapolis', 'Wake Forest',
         'Mooresville', 'Matthews', 'Fuquay-Varina'],
    ND: ['Fargo', 'Bismarck', 'Grand Forks', 'Minot', 'West Fargo', 'Williston'],
    OH: ['Cincinnati', 'Toledo', 'Akron', 'Youngstown', 'Lorain', 'Kettering', 'Elyria',
         'Cuyahoga Falls', 'Euclid', 'Mentor', 'Beavercreek', 'Strongsville', 'Westerville',
         'Findlay', 'Sandusky', 'Shaker Heights', 'Chillicothe', 'Zanesville'],
    OK: ['Oklahoma City', 'Tulsa', 'Broken Arrow', 'Lawton', 'Edmond', 'Midwest City', 'Enid',
         'Muskogee', 'Bartlesville', 'Owasso', 'Bixby', 'Jenks', 'Sand Springs', 'Sapulpa',
         'Claremore', 'Stillwater, OK', 'Tahlequah', 'Ponca City', 'Shawnee, OK'],
    OR: ['Gresham', 'Beaverton', 'Corvallis', 'Tigard', 'Lake Oswego', 'Klamath Falls',
         'McMinnville', 'Newberg', 'Tualatin', 'Wilsonville', 'Milwaukie', 'Hillsboro, OR',
         'Portland, OR', 'Eugene, OR', 'Salem, OR', 'Bend, OR'],
    PA: ['Philadelphia', 'Pittsburgh', 'Allentown', 'Scranton', 'Harrisburg', 'Altoona',
         'Wilkes-Barre', 'Bethel Park', 'State College', 'Norristown', 'Bensalem',
         'King of Prussia', 'Doylestown', 'West Chester', 'Pottstown', 'Bala Cynwyd'],
    RI: ['Providence', 'Cranston', 'Pawtucket', 'East Providence', 'Woonsocket', 'Narragansett'],
    SC: ['North Charleston', 'Rock Hill', 'Summerville', 'Spartanburg', 'Myrtle Beach',
         'Hilton Head', 'Goose Creek', 'Aiken', 'Clemson', 'Charleston, SC'],
    SD: ['Sioux Falls', 'Rapid City', 'Vermillion', 'Yankton'],
    TN: ['Nashville', 'Memphis', 'Knoxville', 'Chattanooga', 'Murfreesboro', 'Johnson City',
         'Bartlett', 'Kingsport', 'Collierville', 'Gatlinburg', 'Oak Ridge', 'Cookeville'],
    TX: ['Houston', 'San Antonio', 'Dallas', 'Fort Worth', 'El Paso', 'Corpus Christi', 'Plano',
         'Laredo', 'Lubbock', 'Garland', 'Irving', 'Amarillo', 'Grand Prairie', 'Brownsville',
         'McKinney', 'Frisco', 'Mesquite', 'Killeen', 'McAllen', 'Carrollton', 'Waco', 'Denton',
         'Abilene', 'Round Rock', 'Richardson', 'Wichita Falls', 'League City', 'Pearland',
         'College Station', 'Sugar Land', 'Edinburg', 'Bryan', 'Baytown', 'Pflugerville',
         'Missouri City', 'Flower Mound', 'Harlingen', 'North Richland Hills', 'Conroe',
         'New Braunfels', 'Cedar Park', 'Rowlett', 'Port Arthur', 'Euless', 'Pharr', 'DeSoto',
         'San Angelo', 'Galveston', 'Waxahachie', 'Lewisville', 'Grapevine', 'Keller', 'Coppell',
         'Southlake', 'The Woodlands', 'Katy', 'Humble', 'Austin, TX', 'Arlington, TX'],
    UT: ['Salt Lake City', 'West Valley City', 'Provo', 'West Jordan', 'Orem', 'Ogden',
         'Layton', 'Taylorsville', 'South Jordan', 'Lehi', 'Draper', 'Bountiful', 'Riverton',
         'Herriman', 'Spanish Fork', 'Park City', 'Cedar City', 'Tooele'],
    VT: ['Montpelier', 'Rutland', 'Brattleboro', 'Bennington', 'Stowe', 'South Burlington'],
    VA: ['Virginia Beach', 'Chesapeake', 'Newport News', 'Roanoke', 'Lynchburg', 'Harrisonburg',
         'Charlottesville', 'Manassas', 'Blacksburg', 'Reston', 'McLean', 'Herndon', 'Ashburn',
         'Chantilly', 'Centreville', 'Midlothian', 'Fairfax, VA', 'Alexandria, VA'],
    WA: ['Seattle', 'Spokane', 'Tacoma', 'Renton', 'Yakima', 'Federal Way', 'Spokane Valley',
         'Bellingham', 'Kennewick', 'Kirkland', 'Burien', 'Sammamish', 'Issaquah', 'Puyallup',
         'Walla Walla', 'Wenatchee', 'Bremerton', 'Tukwila', 'Edmonds', 'Bothell', 'Snohomish',
         'Mukilteo', 'Bellevue, WA', 'Olympia, WA'],
    WV: ['Morgantown', 'Parkersburg', 'Martinsburg', 'Beckley', 'Clarksburg', 'Charleston, WV'],
    WI: ['Milwaukee', 'Green Bay', 'Kenosha', 'Racine', 'Appleton', 'Waukesha', 'Oshkosh',
         'Eau Claire', 'Janesville', 'West Allis', 'La Crosse', 'Sheboygan', 'Wauwatosa',
         'Fond du Lac', 'Menomonee Falls', 'Beloit', 'Manitowoc', 'Wausau', 'Stevens Point',
         'Madison, WI'],
    WY: ['Cheyenne', 'Laramie', 'Rock Springs', 'Jackson Hole', 'Cody, Wyoming'],
    DC: ['Washington, D.C.', 'Washington, DC', 'Capitol Hill'],
  };

  // name(lowercase) -> abbr. Built once; a duplicate across two states would be
  // a curation mistake, so it is reported rather than silently resolved.
  const INDEX = new Map();
  const DUPES = [];
  Object.keys(CITIES).forEach(abbr => {
    CITIES[abbr].forEach(city => {
      const key = city.toLowerCase();
      if (INDEX.has(key) && INDEX.get(key) !== abbr) { DUPES.push(city); return; }
      INDEX.set(key, abbr);
    });
  });

  /** Longest-first, so "Kansas City" is never matched as "Kansas". */
  const ORDERED = [...INDEX.keys()].sort((a, b) => b.length - a.length);

  function esc(s){ return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }

  // One alternation, built once. A word boundary each side, because "Katy" must
  // not fire inside "Katydid" and "Erie" must not fire inside "Superior".
  const RE = new RegExp(
    '(^|[^A-Za-z])(' + ORDERED.map(esc).join('|') + ')(?![A-Za-z])',
    'gi'
  );

  /** Every gazetteer city in `text`, as {abbr, city, at, evidence}.
   *
   *  Returns matches, not a verdict. The caller decides what a city mention is
   *  worth — this file only answers "which state is this place in", which is
   *  the part that is a fact rather than a judgement. */
  function findCities(text){
    const hay = String(text || '');
    const out = [];
    RE.lastIndex = 0;
    let m;
    while ((m = RE.exec(hay)) !== null){
      const at = m.index + m[1].length;
      const abbr = INDEX.get(m[2].toLowerCase());
      if (!abbr) continue;
      out.push({ abbr, city: m[2], at,
                 evidence: hay.slice(Math.max(0, at - 40), at + m[2].length + 40)
                              .replace(/\s+/g, ' ').trim() });
      if (out.length > 400) break;
    }
    return out;
  }

  root.spGazetteerCities = findCities;
  root.spGazetteerIndex = INDEX;
  root.spGazetteerDuplicates = DUPES;
})(typeof window !== 'undefined' ? window : globalThis);
